import { Breadcrumb } from "#/components/seo/Breadcrumb.js";
import { Tldr } from "#/components/seo/Tldr.js";
import { SourcesSection } from "#/components/seo/SourcesSection.js";
import { VerifiedAt } from "#/components/seo/VerifiedAt.js";

type Props = {
  slug: string;
  display: string;
  pathogen: unknown;
  locale: string;
};

export { Page };

function Page({ display, pathogen, locale }: Props) {
  const p = (pathogen as
    | {
        description?: string;
        transmission?: string;
        symptoms?: string;
        treatment?: string;
        sourceUrl?: string;
        verifiedAt?: string;
      }
    | null) ?? null;

  const sources = p?.sourceUrl
    ? [{ url: p.sourceUrl, label: "Pathogen reference", verifiedAt: p.verifiedAt }]
    : [];

  return (
    <main id="main-content" className="container mx-auto ps-4 pe-4 py-6 max-w-3xl">
      <Breadcrumb
        items={[
          { name: "Home", href: "/" },
          { name: "Pathogens", href: "/" },
          { name: display },
        ]}
        locale={locale}
      />

      <header className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">{display}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Reference page — symptoms, transmission, and outbreak history.
        </p>
        {p?.verifiedAt ? (
          <div className="mt-2">
            <VerifiedAt isoTimestamp={p.verifiedAt} locale={locale} />
          </div>
        ) : null}
      </header>

      <Tldr>
        {p?.description ??
          `${display} is a member of the hantavirus genus (Hantaviridae) — a rodent-borne pathogen capable of causing severe respiratory or hemorrhagic disease in humans. Transmission is via aerosolised rodent excreta; person-to-person spread is uncommon outside Andes virus outbreaks.`}
      </Tldr>

      {p?.transmission ? (
        <section className="mt-6">
          <h2 className="text-xl font-semibold mb-2">Transmission</h2>
          <p className="text-sm text-foreground/90">{p.transmission}</p>
        </section>
      ) : null}

      {p?.symptoms ? (
        <section className="mt-6">
          <h2 className="text-xl font-semibold mb-2">Symptoms</h2>
          <p className="text-sm text-foreground/90">{p.symptoms}</p>
        </section>
      ) : null}

      {p?.treatment ? (
        <section className="mt-6">
          <h2 className="text-xl font-semibold mb-2">Treatment</h2>
          <p className="text-sm text-foreground/90">{p.treatment}</p>
        </section>
      ) : null}

      <SourcesSection sources={sources} locale={locale} />
    </main>
  );
}
